import '../layout/NewLayout.css';
import './login.css';
import { login } from '../../api';
import { useNavigate } from 'react-router-dom';

import emailIcon from '../../assets/svgs/email-icon.svg';
import lockIcon from '../../assets/svgs/lock-icon.svg';
import enterIcon from '../../assets/svgs/enter-icon.svg';
import { Input } from 'antd';
import appNotificationInit, { appNotification } from "../../components/common/notification";
import orgLogo from "../../assets/orgLogo.png";

declare global {
    interface Window {
        pwdErrorTimer: number | undefined;
        fadeOutTimer: number | undefined;
    }
}
import React, { useState, useCallback, useEffect, useContext } from "react";
import { AppStateContext } from '../../state/AppProvider';
import { useSessionCookie } from '../../components/authentication/UseSessionCookie';
import { COOKIE_NAME_LOGIN_ROLE } from '../../constants/config';
import { UserRole } from '../../constants/enums';

const LoginLayout = () => {
    const navigate = useNavigate();
    const [username, setUsername] = useState<string>('');
    const [password, setPassword] = useState<string>('');
    const [pwdError, setPwdError] = useState<boolean>(false);
    const [fadeOut, setFadeOut] = useState<boolean>(false);
    const [loadingPassword, setLoadingPassword] = useState<boolean>(false);
    const [darkmodeActive, setDarkmodeActive] = useState<boolean>(false);
    const appStateContext = useContext(AppStateContext)
    const [, setloggedInAsRole] = useSessionCookie(COOKIE_NAME_LOGIN_ROLE, undefined);
    const [screenSize, setScreenSize] = useState(getScreenSize());
    

    function getScreenSize() {
        if (window.innerWidth < 768) return 1;
        if (window.innerWidth < 1024) return 2;
        return 3;
    }

    appNotificationInit()

    useEffect(() => {
        localStorage.getItem('darkmode') === '1' ? document.body.classList.add("dark-mode") : document.body.classList.remove("dark-mode");
        setDarkmodeActive(document.body.classList.contains('dark-mode') ?? false)
        const observer = new MutationObserver(() => {
            setDarkmodeActive(document.body.classList.contains("dark-mode"));
        });

        observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    }, [])

    useEffect(() => {
        const handleResize = () => {
            setScreenSize(getScreenSize());
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setUsername(e.target.value);
    };

    const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPassword(e.target.value);
    };

    const handlePwdError = useCallback(() => {
        // Reset any ongoing timers
        if (window.pwdErrorTimer) {
            clearTimeout(window.pwdErrorTimer);
        }
        if (window.fadeOutTimer) {
            clearTimeout(window.fadeOutTimer);
        }

        // Immediate state reset
        setPwdError(false);
        setFadeOut(false);

        // Start new animation cycle
        setPwdError(true);
        window.pwdErrorTimer = setTimeout(() => {
            setFadeOut(true);
            window.fadeOutTimer = setTimeout(() => {
                setPwdError(false);
                setFadeOut(false);
            }, 1000);
        }, 2000);
    }, []);

    const handleSubmit = useCallback(() => {
        setLoadingPassword(true);
        if (!username || !password) {
            console.error("Username and password are required");
            handlePwdError();
            setLoadingPassword(false);
            return;
        }

        login(username, password)
            .then((response: any) => {
                if (response.ok) {
                    setLoadingPassword(false);
                    return response.json();
                } else {
                    console.error("Login failed");
                    setloggedInAsRole(undefined);
                    setLoadingPassword(false);
                    handlePwdError();
                    // Show error message to the user
                }
            })
            .then((data: any) => {
                if (!data) return;
                const user = data as User
                setloggedInAsRole(user.role)
                appStateContext?.dispatch({ type: "SET_USER_LOGIN_STATUS", payload: user.role });
                // if(user.role === UserRole.Admin){
                //     navigate("/admin");
                // }
                // else{
                navigate("/");
                // }
            }).catch((err: any) => {
                setLoadingPassword(false);
            });

    }, [username, password]);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Enter") {
                handleSubmit();
            }
        };
    
        window.addEventListener("keydown", handleKeyDown);
    
        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [handleSubmit]);

    return (
        <div
            className='login-bg'
            style={{
                height: '100vh',
                width: '100vw',
                backgroundAttachment: 'fixed',
                backgroundRepeat: 'no-repeat',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
            }}
        >
            <div className="login-container">
                {/* Left Section: Login Box */}
                <div className="login-left" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
  
                    {import.meta.env.VITE_SHOW_ORG_LOGO == 'ENABLED' && <img
                        src={orgLogo}
                        className="login-org-logo filter-normal-white"
                        alt="Organisation Logo"
                    />}

                    {/* Bold text below logo */}
                    <div className='login-logo-subtext'>{import.meta.env.VITE_APP_TITLE}</div>

                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '14px',
                                boxShadow: '0px 6px 4px -2px #392EA40A, 2px 2px 4px 0px #392EA414',
                                borderRadius: '20px',
                                padding: '20px',
                                backgroundColor: 'white',
                            }}
                            className={`bg-white b-white-thick ${screenSize > 2 ? 'login-min-width' : ''}`}
                        >
                            <h1 className='fs40-fw800 mt-2 mb-2 c-default' style={{ textAlign: 'center' }}>Sign In</h1>
                            <Input
                                autoComplete="none"
                                placeholder="User ID"
                                className='login-input bg-white c-default fs16-fw600 c-off-default'
                                onChange={handleUsernameChange}
                                prefix={<img src={emailIcon} alt="" />}
                            />
                            <Input.Password
                                autoComplete="none"
                                placeholder="Password"
                                className='login-input bg-white c-default fs16-fw600 c-off-default'
                                onChange={handlePasswordChange}
                                prefix={<img src={lockIcon} alt="" />}
                            />
                            <button
                                disabled={loadingPassword}
                                className={`w-fit-content d-flex ${darkmodeActive ? 'bg-primary c-root' : 'bg-root c-default'} br-16 mx-auto b-primary cur-pointer`}
                                onClick={(e) => {
                                    const button = e.target as HTMLButtonElement;
                                    handleSubmit()
                                }}
                            >
                                <div className='fs16-fw600 ps-2 pt-1 pb-1'>Login</div>
                                {
                                    loadingPassword ? (
                                        <div className='spinner-login ms-1 my-auto'></div>
                                    ) : (
                                        <img className='my-auto ms-1 pe-2' src={darkmodeActive ? enterIcon : enterIcon} alt="" />
                                    )
                                }
                            </button>
                            {pwdError ? (
                                <div style={{ fontSize: '14px', fontWeight: '400', textAlign: 'center', marginTop: '10px', marginBottom: '0px' }} className={`error-message ${fadeOut ? 'fade-out' : ''}`}>
                                    Incorrect Username or Password
                                </div>
                            ) : (
                                <div style={{ fontSize: '14px', fontWeight: '400', textAlign: 'center', marginTop: '10px', marginBottom: '0px' }} className='error-message'>‎ </div>
                            )
                            }
                        </div>
                    </div>
                </div>
    
                {/* Right Section: Image */}
                <div className="login-right"></div>
            </div>
        </div>
    );
    
    
}

export default LoginLayout;
